import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'
import { isBrowser } from '@backmarket/utils/env/isBrowser'

import { getDeviceId as getAmplitudeDeviceId } from '../amplitude'
import { COOKIES } from '../constants'

import { PLATFORM_MARKET_ATTRIBUTE, SDK_ENDPOINT } from './constants'

export type BrazeInstance = typeof import('@braze/web-sdk')
let instance = <BrazeInstance>{}
let brazeApiKey = ''
let isOptInCookieNeededForBraze = true

let readyPromise: Promise<boolean> | null = null
let resolveReady: ((value: boolean) => void) | null = null
/** Set when setBrazeNotInitializing() is called so whenBrazeIsReady() resolves with null even if no one waited yet. */
let brazeNotInitializing = false

function getOrCreateBrazeSDKReadyPromise(): Promise<boolean> {
  // Braze will not be initialized (e.g. feature disabled) — resolve with null immediately.
  if (brazeNotInitializing) {
    return Promise.resolve(false)
  }

  // Instance already loaded and enabled, resolve with it immediately.
  if (isInstanceAvailable() && !isInstanceDisabled()) {
    return Promise.resolve(true)
  }

  if (readyPromise) {
    return readyPromise
  }

  readyPromise = new Promise<boolean>((resolve) => {
    resolveReady = resolve
  })

  return readyPromise
}

/**
 * Resolves the readyPromise when the Braze instance is ready.
 * Resolves with true if Braze is enabled.
 * Resolves with false if Braze is disabled.
 */
function resolveReadyPromise(value: boolean) {
  if (resolveReady) {
    resolveReady(value)
    resolveReady = null
  }
}

/**
 * Returns a promise that resolves when the Braze instance is ready.
 * Resolves immediately with true if already ready.
 * Resolves with false if Braze is disabled or will not be initialized (after setBrazeNotInitializing).
 */
export function whenBrazeIsReady(): Promise<boolean> {
  return getOrCreateBrazeSDKReadyPromise()
}

/**
 * Call when Braze will not be initialized (e.g. SDK disabled). Resolves any pending whenBrazeIsReady() with false,
 * and ensures future whenBrazeIsReady() calls resolve with false even if they run before any consumer called whenBrazeIsReady().
 */
export function setBrazeNotInitializing(): void {
  brazeNotInitializing = true
  if (resolveReady) {
    resolveReadyPromise(false)
    resolveReady = null
  }
}

async function setUpBrazePlugin(consent: boolean, modalDisplayed?: boolean) {
  const initOptions = {
    baseUrl: SDK_ENDPOINT,
    enableLogging: false,
  }

  if (!modalDisplayed && !consent) {
    setBrazeNotInitializing()
  }

  // even though it is not intuitive, the SDK needs to be enabled/disabled before it is initialized
  if (!consent) {
    instance.disableSDK()
  } else {
    instance.enableSDK()
  }

  instance.initialize(brazeApiKey, initOptions)

  setGoogleAdCustomAttributes({ hasUserConsent: consent })

  return instance
}

function isInstanceAvailable() {
  return Object.keys(instance).length > 0
}

function isInstanceDisabled() {
  return !isInstanceAvailable() || Boolean(instance?.isDisabled())
}

function hasStoredAmplitudeDeviceId() {
  const localStorage = useLocalStorage()
  const amplitudeDeviceId = localStorage.getItem('amplitudeDeviceId')

  return !!amplitudeDeviceId
}

function setAmplitudeDeviceId() {
  const amplitudeDeviceId = getAmplitudeDeviceId()

  if (isInstanceDisabled() || !amplitudeDeviceId) {
    return
  }

  instance
    .getUser()
    ?.setCustomUserAttribute('amplitude_device_id', amplitudeDeviceId)

  const localStorage = useLocalStorage()
  localStorage.setItem('amplitudeDeviceId', amplitudeDeviceId)
}

export function setGoogleAdCustomAttributes({
  hasUserConsent,
}: {
  hasUserConsent: boolean
}) {
  const logger = useLogger()

  if (isInstanceDisabled()) {
    return
  }

  const userHandle = instance.getUser()

  const userDataResult = userHandle?.setCustomUserAttribute(
    '$google_ad_user_data',
    hasUserConsent,
  )
  const personalizationResult = userHandle?.setCustomUserAttribute(
    '$google_ad_personalization',
    hasUserConsent,
  )

  const isBrazeConsentAttributeSent = userDataResult && personalizationResult

  if (!isBrazeConsentAttributeSent) {
    logger.error(
      '[LIF][BrazeConsent] Error while sending user consent to Braze',
      {
        owners: ['bot-squad-lifecycle-front'],
      },
    )
  }
}

function hasStoredPlatformMarket() {
  const localStorage = useLocalStorage()
  const platformMarket = localStorage.getItem('braze_platform_market_tracked')

  return !!platformMarket
}

function setPlatformMarket() {
  if (isInstanceDisabled()) {
    return
  }

  const { market } = useMarketplace()
  const { countryCode } = market

  const localStorage = useLocalStorage()
  const storedCountryCode = localStorage.getItem(
    'braze_platform_market_tracked',
  )

  // avoid sending the same data over and over
  if (storedCountryCode === countryCode) {
    return
  }

  const userHandle = instance.getUser()

  if (userHandle) {
    userHandle.setCustomUserAttribute(PLATFORM_MARKET_ATTRIBUTE, countryCode)
    localStorage.setItem('braze_platform_market_tracked', countryCode)
  }
}

export function setUserContext({ crmId }: { crmId?: string }) {
  if (isInstanceDisabled()) {
    return
  }

  if (crmId) {
    instance.changeUser(crmId)
    instance.openSession()
  }

  if (!hasStoredAmplitudeDeviceId()) {
    setAmplitudeDeviceId()
  }

  if (!hasStoredPlatformMarket()) {
    setPlatformMarket()
  }
}

export const sendEvent = (
  eventName: string,
  payload: Record<string, unknown>,
) => {
  if (isInstanceDisabled()) {
    return
  }

  instance.logCustomEvent(eventName, payload)
}

type InitBrazeParameters = {
  apiKey: string
  isOptInCookieNeeded: boolean
}

// Initialization called when Didomi is disabled
export const initBraze = async ({
  apiKey,
  isOptInCookieNeeded,
}: InitBrazeParameters) => {
  instance = await import('@braze/web-sdk')
  brazeApiKey = apiKey
  isOptInCookieNeededForBraze = isOptInCookieNeeded

  const areNeededCookiesAccepted =
    getBrowserCookie(COOKIES.userExperience) === 'true' &&
    getBrowserCookie(COOKIES.advertising) === 'true'

  const optIn = isOptInCookieNeededForBraze ? areNeededCookiesAccepted : true

  const result = await setUpBrazePlugin(optIn)
  resolveReadyPromise(optIn)

  return result
}

// Initialization called when Didomi is enabled
export const init = async ({
  apiKey,
  enabled,
  modalDisplayed,
}: {
  apiKey: string
  enabled: boolean
  modalDisplayed: boolean
}) => {
  instance = await import('@braze/web-sdk')
  brazeApiKey = apiKey

  const result = await setUpBrazePlugin(enabled, modalDisplayed)
  if (!modalDisplayed) {
    resolveReadyPromise(enabled)
  }

  return result
}

// called on GDPR update event
export const setOptIn = async (consent: boolean) => {
  brazeNotInitializing = false
  if (!isInstanceAvailable()) {
    return
  }

  if (consent) {
    // Braze plugin needs to be initialized again, only enabling it is not enough
    await setUpBrazePlugin(consent)

    resolveReadyPromise(true)
  } else {
    resolveReadyPromise(false)
    setGoogleAdCustomAttributes({ hasUserConsent: consent })
    instance.removeAllSubscriptions()
    instance.requestImmediateDataFlush()
    instance.disableSDK()
  }
}

export function getInstance(): BrazeInstance | null {
  if (!isBrowser() || isInstanceDisabled()) {
    return null
  }

  return instance
}

/**
 * [LIF-2737] This function can be removed safely in January 2026.
 * Braze service worker has been removed. It now needs to be unregistered from user's browsers.
 */
export function unregisterBrazeServiceWorker() {
  const logger = useLogger()

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations()

        const brazeRegistrations = registrations.filter(
          (registration) =>
            registration.scope.includes('/braze-service-worker.js') ||
            registration.active?.scriptURL.includes('/braze-service-worker.js'),
        )

        const unregisterPromises = brazeRegistrations.map((registration) =>
          registration.unregister(),
        )

        await Promise.all(unregisterPromises)
      } catch (err) {
        const error = err as Error

        logger.error('[LIF][Braze] Error unregistering Braze service worker:', {
          error,
          owners: ['bot-squad-lifecycle-front'],
        })
      }
    })
  }
}
